import React, { useState, useEffect } from 'react';
import { t } from '../lib/i18n';
import { listReports, getReport } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  FileText, AlertTriangle, CheckCircle, XCircle,
  TrendingUp, ArrowRight, Shield
} from 'lucide-react';

export default function Reports({ lang, tenantId }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => { loadReports(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadReports() {
    setLoading(true);
    try {
      const data = await listReports(tenantId);
      setReports(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function viewReport(report) {
    try {
      const full = await getReport(report.id);
      setSelectedReport(full);
      setDetailOpen(true);
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
          {t('reports', lang)}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {lang === 'pl' ? 'Raporty i rekomendacje Adama' : "Adam's reports and recommendations"}
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div>
      ) : reports.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="text-center py-12">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {lang === 'pl' ? 'Brak raport\u00f3w. Wygeneruj raport z negocjacji.' : 'No reports. Generate a report from negotiations.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map(report => (
            <Card
              key={report.id}
              className="border border-border hover:border-border/80 cursor-pointer transition-colors duration-150"
              onClick={() => viewReport(report)}
              data-testid={`report-card-${report.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-medium">{report.negotiation_title || 'Report'}</h3>
                      {report.disagrees_with_supervisor && (
                        <Badge variant="outline" className="text-xs border-red-400/30 text-red-400 gap-1">
                          <AlertTriangle className="w-2.5 h-2.5" />
                          {t('disagrees', lang)}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {report.contractor_name} {' \u2022 '} {formatDate(report.created_at)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                      {report.summary}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <div className="text-right">
                      <div className="text-lg font-semibold tabular-nums text-primary" style={{ fontFamily: 'Space Grotesk' }}>
                        {Math.round((report.confidence_level || 0) * 100)}%
                      </div>
                      <div className="text-xs text-muted-foreground">{t('confidence', lang)}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Report Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedReport && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {selectedReport.negotiation_title || 'Report'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-3">
                {/* Summary */}
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground mb-1">{t('summary', lang)}</h4>
                  <p className="text-sm leading-relaxed">{selectedReport.summary}</p>
                </div>

                <Separator />

                {/* Recommendation */}
                <div className="p-3 rounded-xl bg-primary/5 border border-primary/20">
                  <div className="flex items-center gap-1.5 mb-1">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <h4 className="text-xs font-semibold text-primary">{t('recommendation', lang)}</h4>
                  </div>
                  <p className="text-sm leading-relaxed">{selectedReport.recommendation}</p>
                </div>

                {/* Risks */}
                {selectedReport.risks && selectedReport.risks.length > 0 && (
                  <div data-testid="report-risk-callout">
                    <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                      {t('risks', lang)}
                    </h4>
                    <div className="space-y-1">
                      {selectedReport.risks.map((risk, i) => (
                        <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-red-400/5 border border-red-400/10">
                          <span className="text-xs text-red-400 mt-0.5">{i + 1}.</span>
                          <p className="text-sm text-muted-foreground">{risk}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Disagreement */}
                {selectedReport.disagrees_with_supervisor && (
                  <div className="p-3 rounded-xl bg-red-400/5 border border-red-400/20">
                    <div className="flex items-center gap-1.5 mb-1">
                      <XCircle className="w-4 h-4 text-red-400" />
                      <h4 className="text-xs font-semibold text-red-400">{t('disagreementReason', lang)}</h4>
                    </div>
                    <p className="text-sm leading-relaxed">{selectedReport.disagreement_reason}</p>
                  </div>
                )}

                {/* Assessment */}
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                    <Shield className="w-3.5 h-3.5" />
                    {t('assessment', lang)}
                  </h4>
                  <p className="text-sm leading-relaxed text-muted-foreground">{selectedReport.counterparty_assessment}</p>
                </div>

                {/* Next Steps */}
                {selectedReport.next_steps && selectedReport.next_steps.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground mb-2">{t('nextSteps', lang)}</h4>
                    <div className="space-y-1">
                      {selectedReport.next_steps.map((step, i) => (
                        <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-muted/30">
                          <CheckCircle className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
                          <p className="text-sm">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Confidence */}
                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30">
                  <span className="text-xs text-muted-foreground">{t('confidence', lang)}:</span>
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${(selectedReport.confidence_level || 0) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
                    {Math.round((selectedReport.confidence_level || 0) * 100)}%
                  </span>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
